import { useEffect, useState } from 'react';
import { cn } from '../../lib/utils';
import { services } from '../../services';
import { Avatar, AvatarFallback } from '../ui/avatar';
import { TrendingUp, TrendingDown } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

interface Investor {
  id: string;
  name: string;
  initials: string;
  aum: number;
  change: number;
  tokens: number;
}

interface TopInvestorsProps {
  className?: string;
}

const formatCurrency = (value: number) => {
  if (value >= 1000000) {
    return `$${(value / 1000000).toFixed(1)}M`;
  }
  return `$${(value / 1000).toFixed(0)}K`;
};

const getInitials = (name: string): string => {
  if (!name) return '??';
  const parts = name.trim().split(/\s+/);
  if (parts.length >= 2) {
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  }
  return name.substring(0, 2).toUpperCase();
};

export function TopInvestors({ className }: TopInvestorsProps) {
  const [investors, setInvestors] = useState<Investor[]>([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    fetchTopInvestors();
  }, []);

  const fetchTopInvestors = async () => {
    try {
      setLoading(true);
      const response = await services.get<{
        status: boolean;
        message?: string;
        data: Array<{
          id: string;
          name: string;
          aum: number;
          change: number;
          tokens: number;
        }>;
      }>('/admin/dashboard/top-investors');

      if (response.status === 200 && response.data?.status && response.data?.data) {
        const formattedInvestors = response.data.data.map((investor) => ({
          ...investor,
          initials: getInitials(investor.name),
        }));
        setInvestors(formattedInvestors);
      } else if (response.status === 401) {
        console.error('Authentication required for top investors');
        setInvestors([]);
      } else if (response.status === 403) {
        console.error('Admin access required for top investors');
        setInvestors([]);
      } else {
        setInvestors([]);
      }
    } catch (error: unknown) {
      const axiosError = error as { response?: { status?: number } };
      if (axiosError?.response?.status === 401) {
        console.error('Authentication required for top investors');
        setInvestors([]);
      } else if (axiosError?.response?.status === 403) {
        console.error('Admin access required for top investors');
        setInvestors([]);
      } else {
        console.error('Error fetching top investors:', error);
        setInvestors([]);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={cn('glass-card rounded-xl p-6 animate-slide-up', className)}>
      <div className='flex items-center justify-between mb-6'>
        <h3 className='text-lg font-semibold text-foreground'>Top Investors</h3>
        <button
          onClick={() => navigate('/users')}
          className='text-sm text-primary hover:text-primary/80 transition-colors'
        >
          View All
        </button>
      </div>

      {loading ? (
        <div className='space-y-4'>
          {[1, 2, 3, 4, 5].map((i) => (
            <div key={i} className='flex items-center gap-4 p-3 rounded-lg animate-pulse'>
              <div className='w-4 h-4 bg-muted rounded' />
              <div className='h-10 w-10 bg-muted rounded-full' />
              <div className='flex-1 space-y-2'>
                <div className='h-4 bg-muted rounded w-3/4' />
                <div className='h-3 bg-muted rounded w-1/2' />
              </div>
              <div className='text-right space-y-2'>
                <div className='h-4 bg-muted rounded w-16' />
                <div className='h-3 bg-muted rounded w-12' />
              </div>
            </div>
          ))}
        </div>
      ) : investors.length === 0 ? (
        <div className='flex items-center justify-center py-8 text-muted-foreground'>
          <p>No investor data available</p>
        </div>
      ) : (
        <div className='space-y-4'>
          {investors.map((investor, index) => (
            <div
              key={investor.id}
              className='flex items-center gap-4 p-3 rounded-lg hover:bg-muted/30 transition-colors cursor-pointer'
              onClick={() => navigate(`/users/${investor.id}`)}
            >
              <span className='text-sm font-medium text-muted-foreground w-4'>{index + 1}</span>
              <Avatar className='h-10 w-10 border border-border'>
                <AvatarFallback className='bg-primary/10 text-primary text-sm font-medium'>
                  {investor.initials}
                </AvatarFallback>
              </Avatar>
              <div className='flex-1 min-w-0'>
                <p className='text-sm font-medium text-foreground truncate'>{investor.name}</p>
                <p className='text-xs text-muted-foreground'>
                  {investor.tokens.toLocaleString()} tokens
                </p>
              </div>
              <div className='text-right'>
                <p className='text-sm font-semibold text-foreground'>
                  {formatCurrency(investor.aum)}
                </p>
                <div
                  className={cn(
                    'flex items-center justify-end gap-1 text-xs font-medium',
                    investor.change >= 0 ? 'text-success' : 'text-destructive'
                  )}
                >
                  {investor.change >= 0 ? (
                    <TrendingUp className='w-3 h-3' />
                  ) : (
                    <TrendingDown className='w-3 h-3' />
                  )}
                  {investor.change >= 0 ? '+' : ''}
                  {investor.change}%
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
